import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Plus, Trophy, TrendingUp, TrendingDown, Trash2, Edit2 } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

// Backend URL - Keep from new version but add fallback
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://platform.tahlil.one';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!location.state?.user);
  const [markets, setMarkets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [dailyAnalysis, setDailyAnalysis] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [editingForecast, setEditingForecast] = useState(null);

  // Authentication check - handle both old and new formats
  useEffect(() => {
    // If we have user from location state, use it
    if (location.state?.user) {
      const userData = location.state.user;
      // Check if user is admin (handle both old and new formats)
      const accessLevel = userData.access_level || (userData.user && userData.user.access_level);
        if (accessLevel && accessLevel.toLowerCase() !== 'admin') {
        navigate('/dashboard');
        return;
      }
      setUser(userData.user || userData);
      setIsAuthenticated(true);
      return;
    }
    
    // Otherwise check auth via API
    const checkAuth = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          // Not authenticated, redirect to login
          navigate('/');
          return;
        }
        
        const data = await response.json();
        
        // Handle both response formats
        let userData;
        let accessLevel;
        
        if (data.authenticated && data.user) {
          // New format (fixed server.py)
          userData = data.user;
          accessLevel = data.user.access_level;
        } else if (data.access_level) {
          // Old format (original server.py)
          userData = data;
          accessLevel = data.access_level;
        } else {
          // Invalid format
          navigate('/');
          return;
        }
        
        if (accessLevel && accessLevel.toLowerCase() !== 'admin') {
          navigate('/dashboard');
          return;
        }
        
        setUser(userData);
        setIsAuthenticated(true);
        
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/');
      }
    };
    
    checkAuth();
  }, [navigate, location.state]);

  // Fetch data only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchMarkets();
      fetchAssets();
      fetchUsers();
      fetchForecasts();
    }
  }, [isAuthenticated]);

  const fetchMarkets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/markets`);
      const data = await response.json();
      setMarkets(data);
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assets`);
      const data = await response.json();
      setAssets(data);
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        credentials: 'include'
      });
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    navigate('/');
  };

  const handleCreateMarket = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${BACKEND_URL}/api/markets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name_ar: formData.get('name_ar'),
          name_en: formData.get('name_en'),
          region: formData.get('region')
        })
      });
      if (response.ok) {
        toast.success(t('marketCreated') || 'Market created successfully');
        fetchMarkets();
        e.target.reset();
      }
    } catch (error) {
      toast.error(t('marketFailed') || 'Failed to create market');
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${BACKEND_URL}/api/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          market_id: formData.get('market_id'),
          name_ar: formData.get('name_ar'),
          name_en: formData.get('name_en'),
          type: formData.get('type')
        })
      });
      if (response.ok) {
        toast.success(t('assetCreated') || 'Asset created successfully');
        fetchAssets();
        e.target.reset();
      }
    } catch (error) {
      toast.error(t('assetFailed') || 'Failed to create asset');
    }
  };

  const handleCreateAnalysis = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${BACKEND_URL}/api/analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset_id: formData.get('asset_id'),
          bias: formData.get('bias'),
          key_levels: formData.get('key_levels'),
          scenario_text: formData.get('scenario_text'),
          insight_text: formData.get('insight_text'),
          risk_note: formData.get('risk_note'),
          confidence_level: formData.get('confidence_level')
        })
      });
      if (response.ok) {
        toast.success(t('analysisCreated') || 'Analysis created successfully');
        e.target.reset();
      }
    } catch (error) {
      toast.error(t('analysisFailed') || 'Failed to create analysis');
    }
  };

  const handleManageSubscription = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          user_email: formData.get('user_email'),
          subscription_type: formData.get('subscription_type'),
          duration_days: parseInt(formData.get('duration_days')),
          action: formData.get('action')
        })
      });
      if (response.ok) {
        toast.success(t('subscriptionUpdated') || 'Subscription updated');
        fetchUsers();
        e.target.reset();
        setSelectedUser(null);
      } else {
        toast.error(t('subscriptionFailed') || 'Failed to update subscription');
      }
    } catch (error) {
      toast.error(t('subscriptionFailed') || 'Failed to update subscription');
    }
  };

  const handleSyncGoogleSheets = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    setSyncing(true);
    setSyncResults(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sheets/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          spreadsheet_id: formData.get('spreadsheet_id'),
          range_name: formData.get('range_name') || 'Markets 1!A2:G'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setSyncResults(result);
        toast.success(t('syncSuccess') || 'Sync successful');
        fetchDailyAnalysis();
      } else {
        const error = await response.json();
        toast.error((t('syncFailed') || 'Sync failed') + ': ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error(t('syncFailed') || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const fetchDailyAnalysis = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-analysis?limit=50`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDailyAnalysis(data);
      }
    } catch (error) {
      console.error('Error fetching daily analysis:', error);
    }
  };

  const fetchForecasts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/history/forecasts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setForecasts(data);
      }
    } catch (error) {
      console.error('Error fetching forecasts:', error);
    }
  };

  const handleCreateForecast = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const forecastData = {
      instrument_code: formData.get('instrument_code'),
      market: formData.get('market'),
      forecast_date: formData.get('forecast_date'),
      forecast_direction: formData.get('forecast_direction'),
      entry_price: parseFloat(formData.get('entry_price')),
      forecast_target_price: parseFloat(formData.get('forecast_target_price')),
      notes: formData.get('notes') || null
    };

    // If actual result is provided
    const actualResult = formData.get('actual_result_price');
    const resultDate = formData.get('result_date');
    if (actualResult && resultDate) {
      forecastData.actual_result_price = parseFloat(actualResult);
      forecastData.result_date = resultDate;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/history/forecast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(forecastData)
      });

      if (response.ok) {
        toast.success(language === 'ar' ? 'تم إنشاء التوقع بنجاح' : 'Forecast created successfully');
        fetchForecasts();
        e.target.reset();
      } else {
        const error = await response.json();
        toast.error(error.detail || (language === 'ar' ? 'فشل في إنشاء التوقع' : 'Failed to create forecast'));
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleUpdateForecastResult = async (e) => {
    e.preventDefault();
    if (!editingForecast) return;
    
    const formData = new FormData(e.target);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/history/forecast/${editingForecast.record_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          actual_result_price: parseFloat(formData.get('actual_result_price')),
          result_date: formData.get('result_date'),
          notes: formData.get('notes') || null
        })
      });

      if (response.ok) {
        toast.success(language === 'ar' ? 'تم تحديث النتيجة بنجاح' : 'Result updated successfully');
        fetchForecasts();
        setEditingForecast(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || (language === 'ar' ? 'فشل في التحديث' : 'Update failed'));
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleDeleteForecast = async (recordId) => {
    if (!confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا التوقع؟' : 'Are you sure you want to delete this forecast?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/history/forecast/${recordId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
        fetchForecasts();
      } else {
        toast.error(language === 'ar' ? 'فشل في الحذف' : 'Delete failed');
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US');
    } catch {
      return dateStr;
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-foreground">{t('adminDashboard') || 'Admin Dashboard'}</h1>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="gap-2"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              {t('logout') || 'Logout'}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="markets" className="w-full" dir={language === 'ar' ? 'rtl' : 'ltr'}>
          <TabsList className="grid w-full grid-cols-6 mb-8">
            <TabsTrigger value="markets" data-testid="markets-tab">{t('markets') || 'Markets'}</TabsTrigger>
            <TabsTrigger value="assets" data-testid="assets-tab">{t('assets') || 'Assets'}</TabsTrigger>
            <TabsTrigger value="analysis" data-testid="analysis-tab">{t('analysis') || 'Analysis'}</TabsTrigger>
            <TabsTrigger value="daily" data-testid="daily-tab">{t('dailyAnalysis') || 'Daily Analysis'}</TabsTrigger>
            <TabsTrigger value="forecasts" data-testid="forecasts-tab" className="flex items-center gap-1">
              <Trophy className="w-4 h-4" />
              {language === 'ar' ? 'التوقعات' : 'Forecasts'}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">{t('users') || 'Users'}</TabsTrigger>
          </TabsList>

          <TabsContent value="markets">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    {t('createMarket') || 'Create Market'}
                  </CardTitle>
                  <CardDescription>{t('createMarketDesc') || 'Add a new market to the platform'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateMarket} className="space-y-4">
                    <div>
                      <Label htmlFor="name_ar">{t('nameAr') || 'Name (Arabic)'}</Label>
                      <Input id="name_ar" name="name_ar" required className={language === 'ar' ? 'text-right' : ''} />
                    </div>
                    <div>
                      <Label htmlFor="name_en">{t('nameEn') || 'Name (English)'}</Label>
                      <Input id="name_en" name="name_en" required className={language === 'ar' ? 'text-right' : ''} />
                    </div>
                    <div>
                      <Label htmlFor="region">{t('region') || 'Region'}</Label>
                      <Input id="region" name="region" required className={language === 'ar' ? 'text-right' : ''} placeholder={t('regionPlaceholder') || 'e.g., GCC'} />
                    </div>
                    <Button type="submit" className="w-full" data-testid="create-market-button">
                      {t('createMarketButton') || 'Create Market'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('currentMarkets') || 'Current Markets'}</CardTitle>
                  <CardDescription>{t('currentMarketsDesc') || 'All markets in the system'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3" data-testid="markets-list">
                    {markets.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">{t('noMarkets') || 'No markets yet'}</p>
                    ) : (
                      markets.map((market) => (
                        <div key={market.market_id} className="p-4 border border-border rounded-lg">
                          <h3 className="font-semibold text-foreground">{language === 'ar' ? market.name_ar : market.name_en}</h3>
                          <p className="text-sm text-muted-foreground">{language === 'ar' ? market.name_en : market.name_ar}</p>
                          <p className="text-xs text-muted-foreground mt-1">{t('region') || 'Region'}: {market.region}</p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="assets">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    {t('createAsset') || 'Create Asset'}
                  </CardTitle>
                  <CardDescription>{t('createAssetDesc') || 'Add a new asset to a market'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateAsset} className="space-y-4">
                    <div>
                      <Label htmlFor="asset_market_id">{t('market') || 'Market'}</Label>
                      <select
                        id="asset_market_id"
                        name="market_id"
                        required
                        className={`w-full px-3 py-2 border border-input rounded-md bg-background ${language === 'ar' ? 'text-right' : ''}`}
                      >
                        <option value="">{t('selectMarket') || 'Select Market'}</option>
                        {markets.map((market) => (
                          <option key={market.market_id} value={market.market_id}>
                            {language === 'ar' ? market.name_ar : market.name_en}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="asset_name_ar">{t('nameAr') || 'Name (Arabic)'}</Label>
                      <Input id="asset_name_ar" name="name_ar" required className={language === 'ar' ? 'text-right' : ''} />
                    </div>
                    <div>
                      <Label htmlFor="asset_name_en">{t('nameEn') || 'Name (English)'}</Label>
                      <Input id="asset_name_en" name="name_en" required className={language === 'ar' ? 'text-right' : ''} />
                    </div>
                    <div>
                      <Label htmlFor="asset_type">{t('type') || 'Type'}</Label>
                      <select
                        id="asset_type"
                        name="type"
                        required
                        className={`w-full px-3 py-2 border border-input rounded-md bg-background ${language === 'ar' ? 'text-right' : ''}`}
                      >
                        <option value="stock">{t('stock') || 'Stock'}</option>
                        <option value="index">{t('index') || 'Index'}</option>
                      </select>
                    </div>
                    <Button type="submit" className="w-full" data-testid="create-asset-button">
                      {t('createAssetButton') || 'Create Asset'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('currentAssets') || 'Current Assets'}</CardTitle>
                  <CardDescription>{t('currentAssetsDesc') || 'All assets in the system'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto" data-testid="assets-list">
                    {assets.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">{t('noAssets') || 'No assets yet'}</p>
                    ) : (
                      assets.map((asset) => (
                        <div key={asset.asset_id} className="p-4 border border-border rounded-lg">
                          <h3 className="font-semibold text-foreground">{language === 'ar' ? asset.name_ar : asset.name_en}</h3>
                          <p className="text-sm text-muted-foreground">{language === 'ar' ? asset.name_en : asset.name_ar}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('type') || 'Type'}: {asset.type === 'stock' ? (t('stock') || 'Stock') : (t('index') || 'Index')}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="analysis">
            <Card className="max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  {t('createAnalysis') || 'Create Analysis'}
                </CardTitle>
                <CardDescription>{t('createAnalysisDesc') || 'Create analysis for an asset'}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateAnalysis} className="space-y-4">
                  <div>
                    <Label htmlFor="analysis_asset_id">{t('asset') || 'Asset'}</Label>
                    <select
                      id="analysis_asset_id"
                      name="asset_id"
                      required
                      className={`w-full px-3 py-2 border border-input rounded-md bg-background ${language === 'ar' ? 'text-right' : ''}`}
                    >
                      <option value="">{t('selectAsset') || 'Select Asset'}</option>
                      {assets.map((asset) => (
                        <option key={asset.asset_id} value={asset.asset_id}>
                          {language === 'ar' ? asset.name_ar : asset.name_en}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="bias">{t('bias') || 'Bias'}</Label>
                    <Input id="bias" name="bias" required className={language === 'ar' ? 'text-right' : ''} placeholder={t('biasPlaceholder') || 'e.g., Bullish, Bearish'} />
                  </div>
                  <div>
                    <Label htmlFor="key_levels">{t('keyLevels') || 'Key Levels'}</Label>
                    <Textarea id="key_levels" name="key_levels" required className={language === 'ar' ? 'text-right' : ''} placeholder={t('keyLevelsPlaceholder') || 'Support and resistance levels...'} rows={3} />
                  </div>
                  <div>
                    <Label htmlFor="scenario_text">{t('scenario') || 'Scenario'}</Label>
                    <Textarea id="scenario_text" name="scenario_text" required className={language === 'ar' ? 'text-right' : ''} placeholder={t('scenarioPlaceholder') || 'Market scenario analysis...'} rows={4} />
                  </div>
                  <div>
                    <Label htmlFor="insight_text">{t('higherTFInsight') || 'Higher Timeframe Insight'}</Label>
                    <Textarea id="insight_text" name="insight_text" className={language === 'ar' ? 'text-right' : ''} placeholder={t('higherTFPlaceholder') || 'Higher timeframe analysis...'} rows={3} />
                  </div>
                  <div>
                    <Label htmlFor="risk_note">{t('riskNote') || 'Risk Note'}</Label>
                    <Textarea id="risk_note" name="risk_note" className={language === 'ar' ? 'text-right' : ''} placeholder={t('riskPlaceholder') || 'Risk considerations...'} rows={2} />
                  </div>
                  <div>
                    <Label htmlFor="confidence_level">{t('confidenceLevel') || 'Confidence Level'}</Label>
                    <select
                      id="confidence_level"
                      name="confidence_level"
                      required
                      className={`w-full px-3 py-2 border border-input rounded-md bg-background ${language === 'ar' ? 'text-right' : ''}`}
                    >
                      <option value="Low">{t('confidenceLow') || 'Low'}</option>
                      <option value="Medium">{t('confidenceMedium') || 'Medium'}</option>
                      <option value="High">{t('confidenceHigh') || 'High'}</option>
                    </select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="create-analysis-button">
                    {t('publishAnalysis') || 'Publish Analysis'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="daily">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    {t('syncFromSheets') || 'Sync from Google Sheets'}
                  </CardTitle>
                  <CardDescription>{t('syncFromSheetsDesc') || 'Import daily analysis from Google Sheets'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSyncGoogleSheets} className="space-y-4">
                    <div>
                      <Label htmlFor="spreadsheet_id">{t('spreadsheetId') || 'Spreadsheet ID'}</Label>
                      <Input 
                        id="spreadsheet_id" 
                        name="spreadsheet_id" 
                        required 
                        defaultValue="1GcXY5-SFba417vZXxOAL3uk7HZlzGBZz72-JWj2rNv4"
                        className={language === 'ar' ? 'text-right' : ''} 
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === 'ar' ? 'من عنوان الرابط: docs.google.com/spreadsheets/d/[ID]' : 'From URL: docs.google.com/spreadsheets/d/[ID]'}
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="range_name">{t('sheetRange') || 'Sheet Range'}</Label>
                      <Input 
                        id="range_name" 
                        name="range_name" 
                        defaultValue="Markets 1!A2:G"
                        className={language === 'ar' ? 'text-right' : ''} 
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {language === 'ar' ? 'نطاق البيانات (افتراضي: Sheet1!A2:G)' : 'Data range (default: Markets 1!A2:G)'}
                      </p>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={syncing}
                      data-testid="sync-sheets-button"
                    >
                      {syncing ? (t('syncInProgress') || 'Syncing...') : (t('syncNow') || 'Sync Now')}
                    </Button>
                  </form>

                  {syncResults && (
                    <div className="mt-6 p-4 border border-border rounded-lg space-y-2">
                      <h3 className="font-semibold text-foreground">{t('syncResults') || 'Sync Results'}</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">{t('totalRows') || 'Total Rows'}:</span>
                          <span className="font-semibold ml-2">{syncResults.total_rows}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t('inserted') || 'Inserted'}:</span>
                          <span className="font-semibold ml-2 text-primary">{syncResults.inserted}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t('updated') || 'Updated'}:</span>
                          <span className="font-semibold ml-2 text-secondary">{syncResults.updated}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t('skipped') || 'Skipped'}:</span>
                          <span className="font-semibold ml-2 text-muted-foreground">{syncResults.skipped}</span>
                        </div>
                      </div>
                      {syncResults.errors && syncResults.errors.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold text-destructive mb-1">{t('errors') || 'Errors'}:</p>
                          <div className="max-h-32 overflow-y-auto space-y-1">
                            {syncResults.errors.map((error, idx) => (
                              <p key={idx} className="text-xs text-muted-foreground">{error}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('latestAnalysis') || 'Latest Analysis'}</CardTitle>
                  <CardDescription>{t('viewDailyAnalysis') || 'View imported daily analysis'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={fetchDailyAnalysis} 
                    variant="outline" 
                    size="sm" 
                    className="mb-4"
                  >
                    {language === 'ar' ? 'تحديث' : 'Refresh'}
                  </Button>
                  <div className="space-y-3 max-h-[500px] overflow-y-auto" data-testid="daily-analysis-list">
                    {dailyAnalysis.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        {language === 'ar' ? 'لا توجد تحليلات يومية بعد' : 'No daily analysis yet'}
                      </p>
                    ) : (
                      dailyAnalysis.map((record) => (
                        <div key={record.record_id} className="p-4 border border-border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-foreground">{record.instrument_code}</h3>
                            <span className="text-xs text-muted-foreground">{record.market}</span>
                          </div>
                          <p className="text-sm text-primary font-medium">{record.insight_type}</p>
                          <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                            <div>
                              <span className="font-semibold">{language === 'ar' ? 'السعر:' : 'Price:'}</span> {record.analysis_price}
                            </div>
                            <div>
                              <span className="font-semibold">{language === 'ar' ? 'الهدف:' : 'Target:'}</span> {record.target_price}
                            </div>
                            <div>
                              <span className="font-semibold">{language === 'ar' ? 'حرج:' : 'Critical:'}</span> {record.critical_level}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {new Date(record.analysis_datetime).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Forecasts Tab - History of Success Management */}
          <TabsContent value="forecasts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Create/Update Forecast Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {editingForecast ? <Edit2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                    {editingForecast 
                      ? (language === 'ar' ? 'تحديث نتيجة التوقع' : 'Update Forecast Result')
                      : (language === 'ar' ? 'إنشاء توقع جديد' : 'Create New Forecast')
                    }
                  </CardTitle>
                  <CardDescription>
                    {editingForecast
                      ? (language === 'ar' ? 'أدخل النتيجة الفعلية للتوقع' : 'Enter the actual result for the forecast')
                      : (language === 'ar' ? 'أضف توقع جديد لسجل النجاح' : 'Add a new forecast to the success history')
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {editingForecast ? (
                    <form onSubmit={handleUpdateForecastResult} className="space-y-4">
                      <div className="p-3 bg-muted rounded-lg text-sm space-y-1 mb-4">
                        <p><strong>{language === 'ar' ? 'الأداة' : 'Instrument'}:</strong> {editingForecast.instrument_code}</p>
                        <p><strong>{language === 'ar' ? 'السوق' : 'Market'}:</strong> {editingForecast.market}</p>
                        <p><strong>{language === 'ar' ? 'الاتجاه' : 'Direction'}:</strong> 
                          <span className={editingForecast.forecast_direction === 'Bullish' ? 'text-green-600 ml-1' : 'text-red-600 ml-1'}>
                            {editingForecast.forecast_direction}
                          </span>
                        </p>
                        <p><strong>{language === 'ar' ? 'سعر الدخول' : 'Entry Price'}:</strong> {editingForecast.entry_price}</p>
                        <p><strong>{language === 'ar' ? 'السعر المستهدف' : 'Target Price'}:</strong> {editingForecast.forecast_target_price}</p>
                      </div>

                      <div>
                        <Label htmlFor="actual_result_price">{language === 'ar' ? 'السعر الفعلي' : 'Actual Result Price'} *</Label>
                        <Input 
                          id="actual_result_price" 
                          name="actual_result_price" 
                          type="number" 
                          step="0.01"
                          required 
                        />
                      </div>

                      <div>
                        <Label htmlFor="result_date">{language === 'ar' ? 'تاريخ النتيجة' : 'Result Date'} *</Label>
                        <Input 
                          id="result_date" 
                          name="result_date" 
                          type="date" 
                          required 
                          defaultValue={new Date().toISOString().split('T')[0]}
                        />
                      </div>

                      <div>
                        <Label htmlFor="update_notes">{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
                        <Textarea id="update_notes" name="notes" defaultValue={editingForecast.notes || ''} />
                      </div>

                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1">
                          {language === 'ar' ? 'حفظ النتيجة' : 'Save Result'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setEditingForecast(null)}>
                          {language === 'ar' ? 'إلغاء' : 'Cancel'}
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <form onSubmit={handleCreateForecast} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="instrument_code">{language === 'ar' ? 'رمز الأداة' : 'Instrument Code'} *</Label>
                          <Input id="instrument_code" name="instrument_code" required placeholder="e.g., AAPL" />
                        </div>
                        <div>
                          <Label htmlFor="forecast_market">{language === 'ar' ? 'السوق' : 'Market'} *</Label>
                          <Input id="forecast_market" name="market" required placeholder="e.g., GCC" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="forecast_date">{language === 'ar' ? 'تاريخ التوقع' : 'Forecast Date'} *</Label>
                          <Input 
                            id="forecast_date" 
                            name="forecast_date" 
                            type="date" 
                            required 
                            defaultValue={new Date().toISOString().split('T')[0]}
                          />
                        </div>
                        <div>
                          <Label htmlFor="forecast_direction">{language === 'ar' ? 'الاتجاه' : 'Direction'} *</Label>
                          <select
                            id="forecast_direction"
                            name="forecast_direction"
                            required
                            className="w-full px-3 py-2 border border-input rounded-md bg-background"
                          >
                            <option value="Bullish">{language === 'ar' ? 'صعودي (شراء)' : 'Bullish (Buy)'}</option>
                            <option value="Bearish">{language === 'ar' ? 'هبوطي (بيع)' : 'Bearish (Sell)'}</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="entry_price">{language === 'ar' ? 'سعر الدخول' : 'Entry Price'} *</Label>
                          <Input id="entry_price" name="entry_price" type="number" step="0.01" required />
                        </div>
                        <div>
                          <Label htmlFor="forecast_target_price">{language === 'ar' ? 'السعر المستهدف' : 'Target Price'} *</Label>
                          <Input id="forecast_target_price" name="forecast_target_price" type="number" step="0.01" required />
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <p className="text-sm text-muted-foreground mb-3">
                          {language === 'ar' ? 'اختياري: أضف النتيجة الفعلية إذا كانت متوفرة' : 'Optional: Add actual result if available'}
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="actual_result_price_create">{language === 'ar' ? 'السعر الفعلي' : 'Actual Result Price'}</Label>
                            <Input id="actual_result_price_create" name="actual_result_price" type="number" step="0.01" />
                          </div>
                          <div>
                            <Label htmlFor="result_date_create">{language === 'ar' ? 'تاريخ النتيجة' : 'Result Date'}</Label>
                            <Input id="result_date_create" name="result_date" type="date" />
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="forecast_notes">{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
                        <Textarea id="forecast_notes" name="notes" placeholder={language === 'ar' ? 'أي ملاحظات إضافية...' : 'Any additional notes...'} />
                      </div>

                      <Button type="submit" className="w-full">
                        <Plus className="w-4 h-4 mr-2" />
                        {language === 'ar' ? 'إنشاء التوقع' : 'Create Forecast'}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>

              {/* Forecasts List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    {language === 'ar' ? 'سجل التوقعات' : 'Forecast History'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'ar' ? 'جميع التوقعات المسجلة' : 'All recorded forecasts'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {forecasts.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        {language === 'ar' ? 'لا توجد توقعات مسجلة بعد' : 'No forecasts recorded yet'}
                      </p>
                    ) : (
                      forecasts.map((forecast) => (
                        <div key={forecast.record_id} className="p-4 border border-border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-foreground">{forecast.instrument_code}</h3>
                              <span className={`flex items-center text-sm ${forecast.forecast_direction === 'Bullish' ? 'text-green-600' : 'text-red-600'}`}>
                                {forecast.forecast_direction === 'Bullish' 
                                  ? <TrendingUp className="w-4 h-4 mr-1" />
                                  : <TrendingDown className="w-4 h-4 mr-1" />
                                }
                                {forecast.forecast_direction}
                              </span>
                            </div>
                            <Badge className={
                              forecast.status === 'success' ? 'bg-green-100 text-green-800' :
                              forecast.status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }>
                              {forecast.status === 'success' 
                                ? (language === 'ar' ? 'ناجح' : 'Success')
                                : forecast.status === 'failed'
                                  ? (language === 'ar' ? 'فاشل' : 'Failed')
                                  : (language === 'ar' ? 'قيد الانتظار' : 'Pending')
                              }
                            </Badge>
                          </div>
                          
                          <div className="text-sm text-muted-foreground grid grid-cols-2 gap-2">
                            <p>{language === 'ar' ? 'السوق' : 'Market'}: {forecast.market}</p>
                            <p>{language === 'ar' ? 'التاريخ' : 'Date'}: {formatDate(forecast.forecast_date)}</p>
                            <p>{language === 'ar' ? 'سعر الدخول' : 'Entry'}: {forecast.entry_price}</p>
                            <p>{language === 'ar' ? 'الهدف' : 'Target'}: {forecast.forecast_target_price}</p>
                            {forecast.actual_result_price && (
                              <p>{language === 'ar' ? 'النتيجة' : 'Result'}: {forecast.actual_result_price}</p>
                            )}
                            {forecast.calculated_pl_percent !== null && (
                              <p className={`font-semibold ${forecast.calculated_pl_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                P/L: {forecast.calculated_pl_percent >= 0 ? '+' : ''}{forecast.calculated_pl_percent}%
                              </p>
                            )}
                          </div>

                          <div className="flex gap-2 pt-2 border-t border-border/50">
                            {forecast.status === 'pending' && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => setEditingForecast(forecast)}
                                className="flex-1"
                              >
                                <Edit2 className="w-4 h-4 mr-1" />
                                {language === 'ar' ? 'تحديث النتيجة' : 'Update Result'}
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              onClick={() => handleDeleteForecast(forecast.record_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    {t('manageSubscription') || 'Manage Subscription'}
                  </CardTitle>
                  <CardDescription>{t('activateSubscription') || 'Activate'}, {t('extendSubscription') || 'Extend'}, {t('giftSubscription') || 'Gift'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleManageSubscription} className="space-y-4">
                    <div>
                      <Label htmlFor="user_email">{t('selectUser') || 'Select User'}</Label>
                      <select
                        id="user_email"
                        name="user_email"
                        required
                        className={`w-full px-3 py-2 border border-input rounded-md bg-background ${language === 'ar' ? 'text-right' : ''}`}
                        onChange={(e) => setSelectedUser(users.find(u => u.email === e.target.value))}
                      >
                        <option value="">{t('selectUser') || 'Select User'}</option>
                        {users.map((user) => (
                          <option key={user.user_id} value={user.email}>
                            {user.email} - {user.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedUser && (
                      <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                        <p><strong>{t('subscriptionStatus') || 'Status'}:</strong> {selectedUser.subscription_status || (t('none') || 'None')}</p>
                        <p><strong>{t('subscriptionType') || 'Type'}:</strong> {selectedUser.subscription_type || (t('none') || 'None')}</p>
                        {selectedUser.subscription_end_date && (
                          <p><strong>{t('expiresOn') || 'Expires'}:</strong> {new Date(selectedUser.subscription_end_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}</p>
                        )}
                      </div>
                    )}

                    <div>
                      <Label htmlFor="subscription_type_admin">{t('selectSubscriptionType') || 'Subscription Type'}</Label>
                      <select
                        id="subscription_type_admin"
                        name="subscription_type"
                        required
                        className={`w-full px-3 py-2 border border-input rounded-md bg-background ${language === 'ar' ? 'text-right' : ''}`}
                      >
                        <option value="Beginner">{t('beginner') || 'Beginner'}</option>
                        <option value="Advanced">{t('advanced') || 'Advanced'}</option>
                        <option value="Premium">{t('premium') || 'Premium'}</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="duration_days">{t('durationDays') || 'Duration (days)'}</Label>
                      <Input id="duration_days" name="duration_days" type="number" required min="1" defaultValue="30" className={language === 'ar' ? 'text-right' : ''} />
                    </div>

                    <div>
                      <Label htmlFor="action_type">{t('action') || 'Action'}</Label>
                      <select
                        id="action_type"
                        name="action"
                        required
                        className={`w-full px-3 py-2 border border-input rounded-md bg-background ${language === 'ar' ? 'text-right' : ''}`}
                      >
                        <option value="activate">{t('activate') || 'Activate'} - {language === 'ar' ? 'تفعيل جديد' : 'New activation'}</option>
                        <option value="extend">{t('extend') || 'Extend'} - {language === 'ar' ? 'إضافة مدة للاشتراك الحالي' : 'Add to existing'}</option>
                        <option value="gift">{t('gift') || 'Gift'} - {language === 'ar' ? 'هدية اشتراك' : 'Gift subscription'}</option>
                        <option value="deactivate">{t('deactivate') || 'Deactivate'} - {language === 'ar' ? 'إلغاء فوري' : 'Cancel immediately'}</option>
                      </select>
                    </div>

                    <Button type="submit" className="w-full" data-testid="manage-subscription-button">
                      {t('apply') || 'Apply'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{t('allUsers') || 'All Users'}</CardTitle>
                  <CardDescription>{t('usersList') || 'List of all registered users'}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="users-list">
                    {users.length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">{language === 'ar' ? 'لا يوجد مستخدمين' : 'No users yet'}</p>
                    ) : (
                      users.map((user) => (
                        <div key={user.user_id} className="p-4 border border-border rounded-lg space-y-2">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-foreground">{user.name}</h3>
                            {user.subscription_status === 'active' ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary">{t('active') || 'Active'}</span>
                            ) : user.subscription_status === 'expired' ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-destructive/10 text-destructive">{t('expired') || 'Expired'}</span>
                            ) : (
                              <span className="px-2 py-1 text-xs rounded-full bg-muted text-muted-foreground">{t('none') || 'None'}</span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                          {user.subscription_type && (
                            <p className="text-xs text-muted-foreground">
                              {t('subscriptionType') || 'Type'}: <strong>{user.subscription_type}</strong>
                            </p>
                          )}
                          {user.subscription_end_date && (
                            <p className="text-xs text-muted-foreground">
                              {t('expiresOn') || 'Expires'}: {new Date(user.subscription_end_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                            </p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
